'use client'

import { PUBLIC_LOCALES, toPublicLocale, type PublicLocale } from '@/lib/locale-prefix'

import { useLocale } from 'next-intl'
import { useRouter, usePathname } from 'next/navigation'
import { Check, ChevronDown, Globe2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getEquivalentPath } from '@/lib/equivalent-path'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { useState } from 'react'

interface LanguageSelectorProps {
  /**
   * Mode determines how language switching works:
   * - 'public': Navigate to locale-specific URL (for landing/legal pages)
   * - 'authenticated': Update user preference via API (for dashboard/app pages)
   */
  mode?: 'public' | 'authenticated'
  
  /**
   * Optional className for custom styling
   */
  className?: string

  /**
   * `compact` replaces the full row with a small locale trigger and menu.
   * It is intended for dense navigation surfaces, both public and authenticated.
   */
  variant?: 'inline' | 'compact'
  
  /**
   * Initial language from server (to avoid hydration mismatch)
   * When provided, this takes precedence over context language on initial render
   */
  initialLanguage?: PublicLocale
}

const LANGUAGE_META = {
  es: { label: 'Español', ariaLabel: 'Switch to Spanish' },
  en: { label: 'English', ariaLabel: 'Switch to English' },
  pt: { label: 'Português', ariaLabel: 'Switch to Portuguese' },
  it: { label: 'Italiano', ariaLabel: 'Switch to Italian' },
  fr: { label: 'Français', ariaLabel: 'Switch to French' },
  ja: { label: '日本語', ariaLabel: 'Switch to Japanese' },
  de: { label: 'Deutsch', ariaLabel: 'Auf Deutsch wechseln' },
} satisfies Record<PublicLocale, { label: string; ariaLabel: string }>

const LANGUAGE_CONTROL_LABEL: Record<PublicLocale, string> = {
  es: 'Cambiar idioma. Idioma actual:',
  en: 'Change language. Current language:',
  pt: 'Mudar idioma. Idioma atual:',
  it: 'Cambia lingua. Lingua corrente:',
  fr: 'Changer de langue. Langue actuelle :',
  ja: '言語を変更。現在の言語：',
  de: 'Sprache ändern. Aktuelle Sprache:',
}

const PUBLIC_LANGUAGE_OPTIONS = PUBLIC_LOCALES.map((code) => ({ code, ...LANGUAGE_META[code] }))

export function LanguageSelector({ 
  mode = 'public',
  className = '',
  initialLanguage,
  variant = 'inline',
}: LanguageSelectorProps) {
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const [isLoading, setIsLoading] = useState(false)
  const { preferredLanguage, updateLanguage, isLoading: isAuthLoading } = useAuth()
  const languageOptions = PUBLIC_LANGUAGE_OPTIONS
  
  // For authenticated mode: use initialLanguage on first render to match server,
  // then sync with preferredLanguage from context once it's available
  // For public mode: use locale from next-intl
  const [userLanguage, setUserLanguage] = useState<PublicLocale | null>(null)
  const currentLanguage = mode === 'authenticated'
    ? (userLanguage ?? (isAuthLoading ? initialLanguage : preferredLanguage) ?? preferredLanguage)
    : toPublicLocale(locale)
  const currentOption = languageOptions.find((option) => option.code === currentLanguage)
    ?? languageOptions[0]

  const switchLocale = async (newLocale: PublicLocale) => {
    if (newLocale === currentLanguage) return
    
    setIsLoading(true)

    try {
      if (mode === 'public') {
        // Centralizado en lib/locale-paths: resuelve el prefijo y los segmentos
        // traducidos (hada → tooth-fairy, fada-do-dente) en una sola operación.
        const newPath = getEquivalentPath(pathname, newLocale)

        console.log('🔄 Language switch:', {
          currentLocale: locale,
          newLocale,
          pathname,
          newPath
        })

        // Use window.location for reliable navigation with force reload
        window.location.replace(newPath)
      } else {
        // For authenticated pages: update user preference via context
        await updateLanguage(newLocale)
        setUserLanguage(newLocale)
        // Refresh server components so they re-fetch locale from DB; avoids full reload
        // and prevents Capacitor from opening the system browser
        router.refresh()
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error switching language:', error)
      setIsLoading(false)
    }
  }

  // Determine styling based on mode
  const isPublicMode = mode === 'public'
  const containerClass = isPublicMode 
    ? 'rounded-lg bg-white/10 p-1 backdrop-blur-sm'
    : 'rounded-lg bg-gray-100 dark:bg-gray-700 p-1'
  
  const getButtonClass = (isActive: boolean) => {
    if (isPublicMode) {
      return `
        rounded-md px-3 py-1.5 text-sm font-medium transition-all
        ${isActive 
          ? 'bg-white/20 text-white shadow-sm' 
          : 'text-white/70 hover:text-white hover:bg-white/10'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `
    } else {
      return `
        rounded-md px-3 py-1.5 text-sm font-medium transition-all
        ${isActive 
          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' 
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `
    }
  }

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center', className)}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={isLoading}
              aria-label={`${LANGUAGE_CONTROL_LABEL[currentLanguage]} ${currentOption.label}`}
              className={cn(
                'group inline-flex h-11 min-w-11 items-center justify-center gap-0 rounded-lg border px-2',
                'text-xs font-semibold uppercase tracking-[0.08em] shadow-sm transition-all',
                'sm:h-9 sm:min-w-0 sm:gap-1.5 sm:rounded-full sm:px-2.5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
                isPublicMode
                  ? 'border-white/15 bg-white/10 text-white backdrop-blur-md hover:border-white/25 hover:bg-white/15'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
              )}
            >
              <Globe2
                className={cn(
                  'hidden size-4 sm:block',
                  isPublicMode ? 'text-amber-200' : 'text-muted-foreground'
                )}
                aria-hidden="true"
              />
              <span>{currentOption.code.toUpperCase()}</span>
              <ChevronDown
                className={cn(
                  'hidden size-3.5 transition-transform group-data-[state=open]:rotate-180 sm:block',
                  isPublicMode ? 'text-white/60' : 'text-muted-foreground'
                )}
                aria-hidden="true"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className={cn(
              'min-w-48 rounded-xl p-1.5 shadow-xl',
              isPublicMode
                ? 'border-white/15 bg-popover/95 backdrop-blur-xl'
                : 'border-border bg-popover'
            )}
          >
            {languageOptions.map((option) => {
              const isActive = currentLanguage === option.code

              return (
                <DropdownMenuItem
                  key={option.code}
                  disabled={isLoading}
                  onSelect={() => void switchLocale(option.code)}
                  aria-label={option.ariaLabel}
                  aria-current={isActive ? true : undefined}
                  className={cn(
                    'min-h-10 cursor-pointer rounded-lg px-2.5 text-sm',
                    isActive && (isPublicMode ? 'bg-white/10' : 'bg-accent')
                  )}
                >
                  <span className="flex-1">{option.label}</span>
                  <Check
                    className={cn(
                      'size-4',
                      isPublicMode ? 'text-amber-300' : 'text-primary',
                      isActive ? 'opacity-100' : 'opacity-0'
                    )}
                    aria-hidden="true"
                  />
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center', className)}>
      <div className={`flex items-center gap-1 ${containerClass}`}>
        {languageOptions.map((option) => (
          <button
            key={option.code}
            onClick={() => switchLocale(option.code)}
            disabled={isLoading}
            className={getButtonClass(currentLanguage === option.code)}
            aria-label={option.ariaLabel}
            aria-pressed={currentLanguage === option.code}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
